import { useState, useEffect, useMemo } from 'react';
import { analyticsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { EMPTY_CELL, formatCellValue, formatNumber } from '@/lib/utils';
import { CheckCircle, Clock, FileText, Globe, Database, AlertTriangle, FolderOpen } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import { PageBanner } from '@/components/PageBanner';
import { PageHeader } from '@/components/PageHeader';
import { PageSkeleton } from '@/components/PageSkeleton';
import { RegionalMapView } from '@/components/RegionalMapView';
import { KpiCard as StatCard } from '@/components/KpiCard';
import { AnalyticsTabBar } from '@/components/AnalyticsTabBar';
import { DataTablePagination, DataTableSortHeader, DataTableToolbar } from '@/components/DataTableControls';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  chartTooltipStyle,
  commonCartesianGridProps,
  commonLegendProps,
  commonXAxisProps,
  commonYAxisProps,
  formatShortDateTick,
} from '@/lib/chartTheme';

const REGION_COLORS = {
  NA: 'hsl(var(--chart-1))',
  EMEA: 'hsl(var(--chart-2))',
  APAC: 'hsl(var(--chart-3))',
  LATAM: 'hsl(var(--chart-4))',
  Global: 'hsl(var(--chart-5))',
};

function getRegionColor(region) {
  return REGION_COLORS[region] || 'hsl(var(--primary))';
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const COVERAGE_COLUMNS = [
  { key: 'title', label: 'Document', className: 'min-w-[360px]' },
  { key: 'scope', label: 'Scope', align: 'center' },
  { key: 'coverage', label: 'Coverage', className: 'text-muted-foreground' },
  { key: 'status', label: 'Status', align: 'center' },
  { key: 'pages', label: 'Total Pages', align: 'center' },
  {
    key: 'filename',
    label: 'File',
    align: 'center',
    className: 'text-muted-foreground text-sm font-mono',
  },
];

const REGION_SUMMARY_COLUMNS = [
  { key: 'region', label: 'Region', width: '28%', filterable: true },
  { key: 'documents', label: 'Documents', align: 'center', width: '24%' },
  { key: 'coveragePct', label: '% of Coverage', align: 'center', width: '24%' },
  { key: 'pages', label: 'Total Pages', align: 'center', width: '24%' },
];

const COUNTRY_SUMMARY_COLUMNS = [
  { key: 'country', label: 'Country', width: '24%' },
  { key: 'region', label: 'Region', width: '20%', filterable: true },
  { key: 'documents', label: 'Documents', align: 'center', width: '18%' },
  { key: 'coveragePct', label: '% of Coverage', align: 'center', width: '20%' },
  { key: 'pages', label: 'Total Pages', align: 'center', width: '18%' },
];

const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function safeGroupColor(color) {
  const value = typeof color === 'string' ? color.trim() : '';
  return HEX_COLOR_PATTERN.test(value) ? value : null;
}

function GroupNameCell({ row }) {
  const dotColor = row.isAllEmployees
    ? 'hsl(var(--muted-foreground))'
    : safeGroupColor(row.color) || 'hsl(var(--primary))';

  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} aria-hidden="true" />
      <span className="truncate">{formatCellValue(row.group)}</span>
    </span>
  );
}

function GroupOnlyCell({ row }) {
  if (row.isAllEmployees) {
    return (
      <span
        className="text-muted-foreground"
        title="Documents shared with everyone aren't restricted to a group — they're counted under Visible for every group."
      >
        {EMPTY_CELL}
      </span>
    );
  }
  return formatCellValue(row.groupOnly);
}

const GROUP_SUMMARY_COLUMNS = [
  { key: 'group', label: 'Group', width: '34%', render: (row) => <GroupNameCell row={row} /> },
  { key: 'members', label: 'Members', align: 'center', width: '22%' },
  { key: 'groupOnly', label: 'Group-only', align: 'center', width: '22%', render: (row) => <GroupOnlyCell row={row} /> },
  { key: 'visible', label: 'Visible', align: 'center', width: '22%' },
];

function normalizeDocumentScope(doc) {
  const countries = Array.isArray(doc.countries) ? doc.countries : [];
  const country = doc.country || '';
  const region = doc.region || '';
  const countryCount = countries.length || String(country).split(/[,;|]/).map((item) => item.trim()).filter(Boolean).length;

  if (countryCount > 1) return 'Multi-country';
  if (countryCount === 1) return 'Single-country';
  if (region && region !== 'Global') return 'Regional';
  return 'Global';
}

function getDocumentCoverage(doc) {
  const countries = Array.isArray(doc.countries) ? doc.countries.filter(Boolean) : [];
  if (countries.length > 0) return countries.join(', ');
  return doc.country || doc.region || 'Global';
}

function getDocumentCountryCount(doc) {
  if (Array.isArray(doc.countries) && doc.countries.length > 0) return doc.countries.length;
  if (doc.country) {
    const countries = String(doc.country).split(/[,;|]/).map((item) => item.trim()).filter(Boolean);
    return Math.max(countries.length, 1);
  }
  return doc.region && doc.region !== 'Global' ? 'Regional' : 'Global';
}

function getFileDisplay(doc) {
  const fileType = doc.file_type || doc.fileType;
  if (fileType) return String(fileType).toUpperCase();
  const filename = doc.filename || '';
  const extension = filename.includes('.') ? filename.split('.').pop() : filename;
  return extension ? extension.toUpperCase() : '';
}

function getCoverageStatus(doc) {
  if (doc.status === 'published' || doc.status === 'live') return 'Live';
  if (doc.status === 'approved') return 'Approved';
  if (doc.status === 'pending_review') return 'Review';
  return 'Draft';
}

function CoverageStatusBadge({ status }) {
  const normalized = String(status || 'Draft').toLowerCase();
  const config = normalized === 'live'
    ? { icon: Globe, className: 'bg-success/10 text-success border-success/30' }
    : normalized === 'approved'
      ? { icon: CheckCircle, className: 'bg-primary/10 text-primary border-primary/30' }
      : normalized === 'review'
        ? { icon: Clock, className: 'bg-warning/10 text-warning border-warning/30' }
        : { icon: FileText, className: 'bg-secondary text-muted-foreground border-border' };
  const StatusIcon = config.icon;

  return (
    <Badge className={`inline-flex min-w-24 justify-center gap-1.5 border ${config.className}`}>
      <StatusIcon className="h-3.5 w-3.5" />
      {status || 'Draft'}
    </Badge>
  );
}

function getCoverageSortValue(row, key) {
  if (key === 'pages') return Number(row[key] || 0);
  if (key === 'countriesCovered') {
    const value = Number(row[key]);
    return Number.isFinite(value) ? value : String(row[key] || '').toLowerCase();
  }
  return String(row[key] || '').toLowerCase();
}

function getGapSortValue(row, key) {
  if (key === 'country') return String(row.country || '').toLowerCase();
  if (key === 'region') return String(row.region || '').toLowerCase();
  if (key === 'users') return String(row.users || '').toLowerCase();
  if (key === 'kb_content') return String(row.kb_content || '').toLowerCase();
  return String(row.issue || '').toLowerCase();
}

const NUMERIC_SUMMARY_KEYS = ['documents', 'coveragePct', 'pages', 'members', 'groupOnly', 'visible'];

function getSummarySortValue(row, key) {
  if (NUMERIC_SUMMARY_KEYS.includes(key)) {
    return Number(row[key] || 0);
  }
  return String(row[key] || '').toLowerCase();
}

function CoverageDocumentsTable({
  rows,
  searchValue,
  onSearchChange,
  scopeFilter,
  onScopeFilterChange,
  coverageFilter,
  onCoverageFilterChange,
  fileFilter,
  onFileFilterChange,
  scopeOptions,
  coverageOptions,
  fileOptions,
  sortKey,
  sortDirection,
  onSort,
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) {
  return (
    <div className="space-y-4">
      <DataTableToolbar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search documents..."
        searchTestId="coverage-search"
        className="border-0 bg-transparent p-0"
      >
        <Select value={scopeFilter} onValueChange={onScopeFilterChange}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Scope" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scopes</SelectItem>
            {scopeOptions.map((option) => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={coverageFilter} onValueChange={onCoverageFilterChange}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Coverage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All coverage</SelectItem>
            {coverageOptions.map((option) => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fileFilter} onValueChange={onFileFilterChange}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="File" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All files</SelectItem>
            {fileOptions.map((option) => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DataTableToolbar>

      {rows.length === 0 ? (
        <EmptyState
          icon={Database}
          title="No coverage documents yet"
          description={
            searchValue
              ? 'Try changing your search or filters.'
              : 'Coverage details will appear here once global or country-specific documents are added.'
          }
          variant="panel"
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[900px] data-table">
              <thead className="bg-muted/70">
                <tr className="border-b border-border">
                  {COVERAGE_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className={`p-3 ${column.align === 'right' ? 'text-right' : ''} ${column.align === 'center' ? 'text-center' : ''} ${!column.align ? 'text-left' : ''}`}
                    >
                      <DataTableSortHeader
                        label={column.label}
                        sortKey={column.key}
                        activeSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={onSort}
                        align={column.align}
                        className={column.align ? 'w-full' : undefined}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                    {COVERAGE_COLUMNS.map((column) => (
                      <td
                        key={column.key}
                        className={`p-3 ${column.align === 'right' ? 'text-right' : ''} ${column.align === 'center' ? 'text-center' : ''} ${column.className || ''}`}
                      >
                        {column.key === 'title' ? (
                          <div>
                            <div className="font-medium text-foreground">{formatCellValue(row.title)}</div>
                            {row.shortSummary && (
                              <div className="mt-1 max-w-2xl text-sm font-normal leading-snug text-muted-foreground">
                                {row.shortSummary}
                              </div>
                            )}
                          </div>
                        ) : column.key === 'scope' ? (
                          <Badge variant="secondary" className="whitespace-nowrap">
                            {formatCellValue(row.scope)}
                          </Badge>
                        ) : column.key === 'status' ? (
                          <CoverageStatusBadge status={row.status} />
                        ) : (
                          formatCellValue(row[column.key])
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            itemLabel="coverage rows"
            pageSizeTestId="coverage-page-size"
          />
        </>
      )}
    </div>
  );
}

function CoverageSummaryTable({ columns, rows, emptyTitle, emptyDescription, itemLabel, showTotals = true }) {
  const [searchValue, setSearchValue] = useState('');
  const [filterValue, setFilterValue] = useState('all');
  const [sortKey, setSortKey] = useState(columns[0]?.key || 'region');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filterColumn = columns.find((column) => column.filterable);
  const query = searchValue.trim().toLowerCase();

  const filterOptions = useMemo(() => (
    filterColumn
      ? [...new Set(rows.map((row) => row[filterColumn.key]).filter(Boolean))].sort()
      : []
  ), [filterColumn, rows]);

  const filteredRows = useMemo(() => (
    rows
      .filter((row) => {
        const matchesSearch = !query || columns.some((column) => (
          String(row[column.key] || '').toLowerCase().includes(query)
        ));
        const matchesFilter = !filterColumn || filterValue === 'all' || row[filterColumn.key] === filterValue;
        return matchesSearch && matchesFilter;
      })
      .sort((a, b) => {
        const aValue = getSummarySortValue(a, sortKey);
        const bValue = getSummarySortValue(b, sortKey);
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      })
  ), [columns, filterColumn, filterValue, query, rows, sortDirection, sortKey]);

  const totalDocuments = filteredRows.reduce((sum, row) => sum + Number(row.documents || 0), 0);
  const totalPages = filteredRows.reduce((sum, row) => sum + Number(row.pages || 0), 0);
  const totalPagesCount = Math.max(Math.ceil(filteredRows.length / pageSize), 1);
  const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (page > totalPagesCount) {
      setPage(totalPagesCount);
    }
  }, [page, totalPagesCount]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleFilterChange = (value) => {
    setFilterValue(value);
    setPage(1);
  };

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Database}
        title={emptyTitle}
        description={emptyDescription}
        variant="panel"
        compact
      />
    );
  }

  return (
    <div className="space-y-4">
      <DataTableToolbar
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder={`Search ${itemLabel}...`}
        searchTestId={`${itemLabel}-summary-search`}
        className="border-0 bg-transparent p-0"
      >
        {filterColumn && (
          <Select value={filterValue} onValueChange={handleFilterChange}>
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue placeholder={`All ${filterColumn.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All {filterColumn.label.toLowerCase()}</SelectItem>
              {filterOptions.map((option) => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </DataTableToolbar>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[640px] table-fixed data-table">
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead className="bg-muted/70">
            <tr className="border-b border-border">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`p-3 ${column.align === 'right' ? 'text-right' : ''} ${
                    column.align === 'center' ? 'text-center' : ''
                  } ${!column.align ? 'text-left' : ''}`}
                >
                  <DataTableSortHeader
                    label={column.label}
                    sortKey={column.key}
                    activeSortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    align={column.align}
                    className={column.align ? 'w-full' : undefined}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`p-3 ${column.align === 'right' ? 'text-right tabular-nums' : ''} ${
                      column.align === 'center' ? 'text-center tabular-nums' : ''
                    } ${
                      column.key === 'region' || column.key === 'country' || column.key === 'group'
                        ? 'font-medium text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {column.render
                      ? column.render(row)
                      : column.key === 'coveragePct'
                        ? `${formatNumber(row.coveragePct)}%`
                        : formatCellValue(row[column.key])}
                  </td>
                ))}
              </tr>
            ))}
            {showTotals && filteredRows.length > 0 && (
              <tr className="bg-muted/40 font-semibold text-foreground">
                <td className="p-3" colSpan={Math.max(columns.length - 3, 1)}>Total</td>
                <td className="p-3 text-center tabular-nums">{formatNumber(totalDocuments)}</td>
                <td className="p-3 text-center tabular-nums">100%</td>
                <td className="p-3 text-center tabular-nums">{formatNumber(totalPages)}</td>
              </tr>
            )}
          </tbody>
        </table>
        {filteredRows.length === 0 && (
          <EmptyState
            icon={Database}
            title={`No matching ${itemLabel}`}
            description="Try changing your search or filter."
            variant="plain"
            compact
          />
        )}
      </div>

      <DataTablePagination
        page={page}
        totalPages={totalPagesCount}
        totalItems={filteredRows.length}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={(value) => {
          setPageSize(value);
          setPage(1);
        }}
        itemLabel={itemLabel}
        pageSizeTestId={`${itemLabel}-summary-page-size`}
      />
    </div>
  );
}

function KBUpdatesChart({ rows }) {
  if (!rows?.some((row) => row.uploaded || row.updated || row.live)) {
    return (
      <Card className="bg-card border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">KB Updates Over Time</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Daily uploads, edits, and documents made live over the last 30 days.
          </p>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={Database}
            title="No KB update activity"
            description="Upload, edit, and publish activity will appear here over the last 30 days."
            variant="panel"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">KB Updates Over Time</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Daily uploads, edits, and documents made live over the last 30 days.
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={rows} margin={{ top: 12, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="kbUploaded" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.45} />
                  <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="kbUpdated" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="kbLive" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid {...commonCartesianGridProps} />
              <XAxis {...commonXAxisProps} dataKey="date" tickFormatter={formatShortDateTick} interval={4} />
              <YAxis {...commonYAxisProps} allowDecimals={false} />
              <Tooltip contentStyle={chartTooltipStyle} labelFormatter={formatShortDateTick} />
              <Legend {...commonLegendProps} />
              <Area type="monotone" dataKey="uploaded" name="Uploaded" stroke="hsl(var(--chart-1))" fill="url(#kbUploaded)" strokeWidth={2} />
              <Area type="monotone" dataKey="updated" name="Updated" stroke="hsl(var(--chart-2))" fill="url(#kbUpdated)" strokeWidth={2} />
              <Area type="monotone" dataKey="live" name="Made Live" stroke="hsl(var(--success))" fill="url(#kbLive)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function CountryCoverageLeaderboard({ rows }) {
  const data = rows
    .slice()
    .sort((a, b) => Number(b.documents || 0) - Number(a.documents || 0))
    .slice(0, 12)
    .map((row) => ({
      ...row,
      label: row.country,
      documents: Number(row.documents || 0),
      pages: Number(row.pages || 0),
    }));

  if (data.length === 0) {
    return (
      <EmptyState
        icon={Database}
        title="No country coverage yet"
        description="Country coverage will appear once localized KB content is indexed."
        variant="panel"
      />
    );
  }

  return (
    <Card className="bg-card border-border shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">Country Coverage Leaderboard</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Countries ranked by localized document coverage.
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, left: 12, bottom: 8 }}>
              <CartesianGrid {...commonCartesianGridProps} />
              <XAxis {...commonXAxisProps} type="number" allowDecimals={false} />
              <YAxis {...commonYAxisProps} type="category" dataKey="label" width={140} />
              <Tooltip
                contentStyle={chartTooltipStyle}
                formatter={(value, name) => [formatNumber(value), name]}
                labelFormatter={(value) => `${value}`}
              />
              <Legend {...commonLegendProps} />
              <Bar dataKey="documents" name="Documents" radius={[0, 6, 6, 0]}>
                {data.map((entry) => (
                  <Cell key={entry.country} fill={getRegionColor(entry.region)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function CoverageBubbleChart({ rows }) {
  const data = rows.map((row) => ({
    country: row.country,
    region: row.region,
    documents: Number(row.documents || 0),
    pages: Number(row.pages || 0),
    bubble: Math.max(Number(row.pages || 0), 1),
  }));

  if (data.length === 0) {
    return (
      <EmptyState
        icon={Database}
        title="No document density data"
        description="Document count and page depth will appear once country coverage exists."
        variant="panel"
      />
    );
  }

  return (
    <Card className="bg-card border-border shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">Documents vs Pages</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Spots thin or deep country coverage by comparing document count with page volume.
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 16, right: 24, left: 0, bottom: 12 }}>
              <CartesianGrid {...commonCartesianGridProps} />
              <XAxis {...commonXAxisProps} type="number" dataKey="documents" name="Documents" allowDecimals={false} />
              <YAxis {...commonYAxisProps} type="number" dataKey="pages" name="Pages" allowDecimals={false} />
              <ZAxis type="number" dataKey="bubble" range={[80, 480]} />
              <Tooltip
                contentStyle={chartTooltipStyle}
                cursor={{ strokeDasharray: '3 3' }}
                formatter={(value, name) => [formatNumber(value), name]}
                labelFormatter={(_value, payload) => payload?.[0]?.payload?.country || ''}
              />
              <Scatter name="Countries" data={data}>
                {data.map((entry) => (
                  <Cell key={entry.country} fill={getRegionColor(entry.region)} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function CoverageGapHeatmap({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <Card className="bg-card border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-foreground">Coverage Gap Heatmap</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Highlights countries where employee presence and localized KB content are out of balance.
          </p>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={AlertTriangle}
            title="No user/content coverage gaps"
            description="Countries with mismatched user presence and KB coverage will appear here."
            variant="panel"
          />
        </CardContent>
      </Card>
    );
  }

  const signalClass = (value) => (
    value === 'Yes'
      ? 'border-success/40 bg-success/20 text-success'
      : 'border-warning/40 bg-warning/20 text-warning'
  );
  const gapClass = (issue) => (
    String(issue || '').toLowerCase().includes('without users')
      ? 'border-info/40 bg-info/20 text-info'
      : 'border-warning/40 bg-warning/20 text-warning'
  );

  return (
    <Card className="bg-card border-border shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground">Coverage Gap Heatmap</CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Highlights countries where employee presence and localized KB content are out of balance.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="grid grid-cols-[minmax(160px,1.3fr)_repeat(3,minmax(120px,1fr))] gap-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <div>Country</div>
            <div className="text-center">Users</div>
            <div className="text-center">KB Content</div>
            <div className="text-center">Gap</div>
          </div>

          <div className="space-y-2">
            {rows.slice(0, 12).map((row) => (
              <div
                key={`${row.country}-${row.region}-${row.issue}`}
                className="grid grid-cols-[minmax(160px,1.3fr)_repeat(3,minmax(120px,1fr))] gap-2"
              >
                <div className="rounded-xl border border-border bg-muted/30 p-4">
                  <div className="font-semibold text-foreground">{formatCellValue(row.country)}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{formatCellValue(row.region)}</div>
                </div>
                <div className={`flex min-h-20 items-center justify-center rounded-xl border p-4 text-center text-sm font-semibold ${signalClass(row.users)}`}>
                  {formatCellValue(row.users || 'No')}
                  {Number(row.user_count || 0) > 0 ? ` (${formatNumber(row.user_count)})` : ''}
                </div>
                <div className={`flex min-h-20 items-center justify-center rounded-xl border p-4 text-center text-sm font-semibold ${signalClass(row.kb_content)}`}>
                  {formatCellValue(row.kb_content || 'No')}
                  {Number(row.document_count || 0) > 0 ? ` (${formatNumber(row.document_count)})` : ''}
                </div>
                <div className={`flex min-h-20 items-center justify-center rounded-xl border p-4 text-center text-sm font-semibold ${gapClass(row.issue)}`}>
                  {formatCellValue(row.issue)}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 pt-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm bg-success/40" />
              Present
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm bg-warning/40" />
              Missing
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm bg-info/40" />
              Content surplus
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ContentGapsTable({
  rows,
  searchValue,
  onSearchChange,
  regionFilter,
  onRegionFilterChange,
  issueFilter,
  onIssueFilterChange,
  regionOptions,
  issueOptions,
  sortKey,
  sortDirection,
  onSort,
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) {
  return (
    <div className="space-y-4">
      <DataTableToolbar
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search gaps..."
        searchTestId="kb-gap-search"
        className="border-0 bg-transparent p-0"
      >
        <Select value={regionFilter} onValueChange={onRegionFilterChange}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All regions</SelectItem>
            {regionOptions.map((option) => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={issueFilter} onValueChange={onIssueFilterChange}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Gap" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All gaps</SelectItem>
            {issueOptions.map((option) => (
              <SelectItem key={option} value={option}>{option}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </DataTableToolbar>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[900px] data-table">
          <thead className="bg-muted/70">
            <tr className="border-b border-border">
              <th className="p-3 text-left">
                <DataTableSortHeader
                  label="Country"
                  sortKey="country"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              <th className="p-3 text-left">
                <DataTableSortHeader
                  label="Region"
                  sortKey="region"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
              </th>
              <th className="p-3 text-center">
                <DataTableSortHeader
                  label="Users"
                  sortKey="users"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={onSort}
                  align="center"
                  className="w-full"
                />
              </th>
              <th className="p-3 text-center">
                <DataTableSortHeader
                  label="KB Content"
                  sortKey="kb_content"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={onSort}
                  align="center"
                  className="w-full"
                />
              </th>
              <th className="p-3 text-center">
                <DataTableSortHeader
                  label="Gap"
                  sortKey="issue"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={onSort}
                  align="center"
                  className="w-full"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((gap, index) => (
              <tr key={`${gap.country || 'gap'}-${gap.region || 'region'}-${gap.issue || 'issue'}-${index}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                <td className="p-3 font-medium text-foreground">{formatCellValue(gap.country)}</td>
                <td className="p-3 text-muted-foreground">{formatCellValue(gap.region)}</td>
                <td className="p-3 text-center">
                  <Badge
                    variant="outline"
                    className={gap.users === 'Yes' ? 'border-success/30 bg-success/10 text-success' : 'border-muted-foreground/25 bg-muted/50 text-muted-foreground'}
                  >
                    {formatCellValue(gap.users || 'No')}
                    {Number(gap.user_count || 0) > 0 ? ` (${formatNumber(gap.user_count)})` : ''}
                  </Badge>
                </td>
                <td className="p-3 text-center">
                  <Badge
                    variant="outline"
                    className={gap.kb_content === 'Yes' ? 'border-success/30 bg-success/10 text-success' : 'border-warning/30 bg-warning/10 text-warning'}
                  >
                    {formatCellValue(gap.kb_content || 'No')}
                    {Number(gap.document_count || 0) > 0 ? ` (${formatNumber(gap.document_count)})` : ''}
                  </Badge>
                </td>
                <td className="p-3 text-center">
                  <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
                    {formatCellValue(gap.issue)}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <EmptyState
            icon={AlertTriangle}
            title="No matching gaps"
            description="Adjust search or filters to see more gaps."
            variant="plain"
            compact
          />
        )}
      </div>

      <DataTablePagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        itemLabel="gaps"
        pageSizeTestId="kb-gap-page-size"
      />
    </div>
  );
}

export default function KnowledgeBasePage() {
  const { hasPermission } = useAuth();
  const canManageKnowledgeBase = hasPermission('kb_manage');
  const [data, setData] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [mapWindow, setMapWindow] = useState('7d');
  const [coverageSearch, setCoverageSearch] = useState('');
  const [coverageScopeFilter, setCoverageScopeFilter] = useState('all');
  const [coverageFilter, setCoverageFilter] = useState('all');
  const [coverageFileFilter, setCoverageFileFilter] = useState('all');
  const [coverageSortKey, setCoverageSortKey] = useState('title');
  const [coverageSortDirection, setCoverageSortDirection] = useState('asc');
  const [coveragePage, setCoveragePage] = useState(1);
  const [coveragePageSize, setCoveragePageSize] = useState(10);
  const [gapSearch, setGapSearch] = useState('');
  const [gapRegionFilter, setGapRegionFilter] = useState('all');
  const [gapIssueFilter, setGapIssueFilter] = useState('all');
  const [gapSortKey, setGapSortKey] = useState('country');
  const [gapSortDirection, setGapSortDirection] = useState('asc');
  const [gapPage, setGapPage] = useState(1);
  const [gapPageSize, setGapPageSize] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await analyticsAPI.getKnowledgeBase();
        setData(result);
      } catch (err) {
        console.error('Failed to fetch KB data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchMapData = async () => {
      if (!data || activeTab !== 'world') return;

      setMapLoading(true);
      try {
        const result = await analyticsAPI.getKnowledgeBase(mapWindow);
        if (cancelled) return;
        setMapData(result);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch KB map data:', err);
        setMapData(null);
      }
      setMapLoading(false);
    };
    fetchMapData();
    return () => {
      cancelled = true;
    };
  }, [activeTab, data, mapWindow]);

  const { executive_snapshot, regional_coverage, country_coverage, global_documents, gaps, kb_updates_over_time, group_coverage } = data || {};
  const mapRegionalCoverage = mapData?.regional_coverage || regional_coverage || [];
  const mapCountryCoverage = mapData?.country_coverage || country_coverage || [];
  const coverageRows = useMemo(() => [
    ...(global_documents || []).map((doc, idx) => ({
      id: `coverage-${doc.title || idx}`,
      title: doc.title,
      shortSummary: doc.short_summary || doc.shortSummary || doc.summary || doc.description || '',
      scope: normalizeDocumentScope(doc),
      coverage: getDocumentCoverage(doc),
      countriesCovered: getDocumentCountryCount(doc),
      status: getCoverageStatus(doc),
      pages: doc.pages,
      filename: getFileDisplay(doc),
    })),
  ], [global_documents]);
  const regionSummaryRows = useMemo(() => {
    const rows = (regional_coverage || []).map((region) => {
      const documents = Number(region.local_docs || 0);
      return {
        id: `region-${region.region}`,
        region: region.region || 'N/A',
        documents,
        pages: Number(region.pages || 0),
      };
    });
    const totalDocuments = rows.reduce((sum, row) => sum + row.documents, 0);
    return rows.map((row) => ({
      ...row,
      coveragePct: totalDocuments > 0 ? Math.round((row.documents / totalDocuments) * 1000) / 10 : 0,
    }));
  }, [regional_coverage]);
  const countrySummaryRows = useMemo(() => {
    const rows = (country_coverage || []).map((country) => ({
      id: `country-${country.country}-${country.region}`,
      country: country.country || 'N/A',
      region: country.region || 'N/A',
      documents: Number(country.documents || 0),
      pages: Number(country.pages || 0),
    }));
    const totalDocuments = rows.reduce((sum, row) => sum + row.documents, 0);
    return rows.map((row) => ({
      ...row,
      coveragePct: totalDocuments > 0 ? Math.round((row.documents / totalDocuments) * 1000) / 10 : 0,
    }));
  }, [country_coverage]);
  const groupSummaryRows = useMemo(() => {
    const raw = Array.isArray(group_coverage?.groups) ? group_coverage.groups : [];
    return raw.map((group, idx) => {
      const isAllEmployees = Boolean(group.is_all_employees);
      const groupOnly = Number(group.group_only_documents ?? 0);
      return {
        id: `group-${group.group_id ?? group.slug ?? idx}`,
        group: group.name || 'N/A',
        members: Number(group.member_count ?? 0),
        groupOnly: isAllEmployees ? null : groupOnly,
        visible: Number(group.visible_documents ?? groupOnly),
        color: group.color || null,
        isAllEmployees,
      };
    });
  }, [group_coverage]);
  const scopeOptions = useMemo(() => (
    [...new Set(coverageRows.map((row) => row.scope).filter(Boolean))].sort()
  ), [coverageRows]);
  const coverageOptions = useMemo(() => (
    [...new Set(coverageRows.map((row) => row.coverage).filter(Boolean))].sort()
  ), [coverageRows]);
  const fileOptions = useMemo(() => (
    [...new Set(coverageRows.map((row) => row.filename).filter(Boolean))].sort()
  ), [coverageRows]);
  const filteredCoverageRows = useMemo(() => {
    const query = coverageSearch.trim().toLowerCase();
    return coverageRows
      .filter((row) => {
        const matchesSearch = !query || [row.title, row.shortSummary, row.scope, row.coverage, row.status, row.filename]
          .some((value) => String(value || '').toLowerCase().includes(query));
        const matchesScope = coverageScopeFilter === 'all' || row.scope === coverageScopeFilter;
        const matchesCoverage = coverageFilter === 'all' || row.coverage === coverageFilter;
        const matchesFile = coverageFileFilter === 'all' || row.filename === coverageFileFilter;
        return matchesSearch && matchesScope && matchesCoverage && matchesFile;
      })
      .sort((a, b) => {
        const aValue = getCoverageSortValue(a, coverageSortKey);
        const bValue = getCoverageSortValue(b, coverageSortKey);
        if (aValue < bValue) return coverageSortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return coverageSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
  }, [coverageRows, coverageSearch, coverageScopeFilter, coverageFilter, coverageFileFilter, coverageSortKey, coverageSortDirection]);
  const coverageTotalPages = Math.max(Math.ceil(filteredCoverageRows.length / coveragePageSize), 1);
  const pagedCoverageRows = filteredCoverageRows.slice((coveragePage - 1) * coveragePageSize, coveragePage * coveragePageSize);
  const gapRegionOptions = useMemo(() => (
    [...new Set((gaps || []).map((gap) => gap.region).filter(Boolean))].sort()
  ), [gaps]);
  const gapIssueOptions = useMemo(() => (
    [...new Set((gaps || []).map((gap) => gap.issue).filter(Boolean))].sort()
  ), [gaps]);
  const filteredGapRows = useMemo(() => {
    const query = gapSearch.trim().toLowerCase();
    return (gaps || [])
      .filter((gap) => {
        const matchesSearch = !query || [gap.country, gap.region, gap.users, gap.kb_content, gap.issue]
          .some((value) => String(value || '').toLowerCase().includes(query));
        const matchesRegion = gapRegionFilter === 'all' || gap.region === gapRegionFilter;
        const matchesIssue = gapIssueFilter === 'all' || gap.issue === gapIssueFilter;
        return matchesSearch && matchesRegion && matchesIssue;
      })
      .sort((a, b) => {
        const aValue = getGapSortValue(a, gapSortKey);
        const bValue = getGapSortValue(b, gapSortKey);
        if (aValue < bValue) return gapSortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return gapSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
  }, [gaps, gapSearch, gapRegionFilter, gapIssueFilter, gapSortKey, gapSortDirection]);
  const gapTotalPages = Math.max(Math.ceil(filteredGapRows.length / gapPageSize), 1);
  const pagedGapRows = filteredGapRows.slice((gapPage - 1) * gapPageSize, gapPage * gapPageSize);

  useEffect(() => {
    if (coveragePage > coverageTotalPages) {
      setCoveragePage(coverageTotalPages);
    }
  }, [coveragePage, coverageTotalPages]);

  useEffect(() => {
    if (gapPage > gapTotalPages) {
      setGapPage(gapTotalPages);
    }
  }, [gapPage, gapTotalPages]);

  const handleCoverageSort = (key) => {
    if (coverageSortKey === key) {
      setCoverageSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setCoverageSortKey(key);
      setCoverageSortDirection('asc');
    }
    setCoveragePage(1);
  };

  const handleCoverageSearchChange = (value) => {
    setCoverageSearch(value);
    setCoveragePage(1);
  };

  const handleCoverageScopeFilterChange = (value) => {
    setCoverageScopeFilter(value);
    setCoveragePage(1);
  };

  const handleCoverageFilterChange = (value) => {
    setCoverageFilter(value);
    setCoveragePage(1);
  };

  const handleCoverageFileFilterChange = (value) => {
    setCoverageFileFilter(value);
    setCoveragePage(1);
  };

  const handleCoveragePageSizeChange = (value) => {
    setCoveragePageSize(value);
    setCoveragePage(1);
  };

  const handleGapSort = (key) => {
    if (gapSortKey === key) {
      setGapSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setGapSortKey(key);
      setGapSortDirection('asc');
    }
    setGapPage(1);
  };

  const handleGapSearchChange = (value) => {
    setGapSearch(value);
    setGapPage(1);
  };

  const handleGapRegionFilterChange = (value) => {
    setGapRegionFilter(value);
    setGapPage(1);
  };

  const handleGapIssueFilterChange = (value) => {
    setGapIssueFilter(value);
    setGapPage(1);
  };

  const handleGapPageSizeChange = (value) => {
    setGapPageSize(value);
    setGapPage(1);
  };

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="6xl"
        banner
        bannerPageKey="kb-coverage"
        kpis={4}
        tabs
        table
        sections={canManageKnowledgeBase ? 2 : 1}
        sectionClassName="h-80"
        data-testid="knowledge-base-page-loading"
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8 animate-fade-in" data-testid="knowledge-base-page">
      {/* Page Banner */}
      <PageBanner pageKey="kb-coverage" />
      
      <PageHeader title="Coverage" description="Knowledge base scale and regional distribution" />

      {/* Executive Snapshot */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-4">Executive Snapshot</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 sm:gap-6">
          <StatCard
            title="Total KB Documents"
            value={executive_snapshot?.total_documents || 0}
            icon={FileText}
          />
          <StatCard
            title="Global Documents"
            value={`${executive_snapshot?.global_pct || 0}%`}
            subtitle={`${executive_snapshot?.global_documents || 0} global · ${(executive_snapshot?.total_documents || 0) - (executive_snapshot?.global_documents || 0)} country-specific`}
            icon={Globe}
            color="emerald"
          />
          <StatCard
            title="Countries Covered"
            value={executive_snapshot?.countries_covered || 0}
            icon={Database}
            color="purple"
          />
          <StatCard
            title="Total Pages"
            value={formatNumber(executive_snapshot?.total_pages || 0)}
            subtitle={`Avg ${executive_snapshot?.avg_pages_per_doc || 0} pages per doc`}
            icon={FolderOpen}
            color="amber"
          />
        </div>
      </section>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <AnalyticsTabBar />

        <TabsContent value="details" className="space-y-6">
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">Coverage Documents</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Documents included in coverage, grouped by global, regional, single-country, and multi-country scope.
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <CoverageDocumentsTable
                rows={pagedCoverageRows}
                searchValue={coverageSearch}
                onSearchChange={handleCoverageSearchChange}
                scopeFilter={coverageScopeFilter}
                onScopeFilterChange={handleCoverageScopeFilterChange}
                coverageFilter={coverageFilter}
                onCoverageFilterChange={handleCoverageFilterChange}
                fileFilter={coverageFileFilter}
                onFileFilterChange={handleCoverageFileFilterChange}
                scopeOptions={scopeOptions}
                coverageOptions={coverageOptions}
                fileOptions={fileOptions}
                sortKey={coverageSortKey}
                sortDirection={coverageSortDirection}
                onSort={handleCoverageSort}
                page={coveragePage}
                totalPages={coverageTotalPages}
                totalItems={filteredCoverageRows.length}
                pageSize={coveragePageSize}
                onPageChange={setCoveragePage}
                onPageSizeChange={handleCoveragePageSizeChange}
              />
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-6">
            <Card className="bg-card border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-foreground">
                  Coverage by Region
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Single-country documents grouped by region with coverage share and page totals.
                </p>
              </CardHeader>
              <CardContent>
                <CoverageSummaryTable
                  columns={REGION_SUMMARY_COLUMNS}
                  rows={regionSummaryRows}
                  emptyTitle="No regional coverage yet"
                  emptyDescription="Regional document coverage will appear once localized KB content is indexed."
                  itemLabel="regions"
                />
              </CardContent>
            </Card>

            <Card className="bg-card border-border shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-foreground">
                  Coverage by Country
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Country-specific KB coverage with document counts and live page volume.
                </p>
              </CardHeader>
              <CardContent>
                <CoverageSummaryTable
                  columns={COUNTRY_SUMMARY_COLUMNS}
                  rows={countrySummaryRows}
                  emptyTitle="No country coverage yet"
                  emptyDescription="Country coverage will appear once localized KB content is indexed."
                  itemLabel="countries"
                />
              </CardContent>
            </Card>

            {canManageKnowledgeBase && groupSummaryRows.length > 0 && (
              <Card className="bg-card border-border shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    Coverage by Group
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Document visibility by user group. Group-only counts documents restricted to that group;
                    Visible adds documents shared with everyone, so it is what a member of the group can actually see.
                  </p>
                </CardHeader>
                <CardContent>
                  <CoverageSummaryTable
                    columns={GROUP_SUMMARY_COLUMNS}
                    rows={groupSummaryRows}
                    emptyTitle="No group coverage yet"
                    emptyDescription="Group coverage appears once documents are restricted to a group in Knowledge Base > Content."
                    itemLabel="groups"
                    showTotals={false}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {canManageKnowledgeBase && gaps && gaps.length > 0 && (
            <Card className="bg-card border-border shadow-sm border-l-4 border-l-amber-400">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning" />
                  User vs KB Content Gaps
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ContentGapsTable
                  rows={pagedGapRows}
                  searchValue={gapSearch}
                  onSearchChange={handleGapSearchChange}
                  regionFilter={gapRegionFilter}
                  onRegionFilterChange={handleGapRegionFilterChange}
                  issueFilter={gapIssueFilter}
                  onIssueFilterChange={handleGapIssueFilterChange}
                  regionOptions={gapRegionOptions}
                  issueOptions={gapIssueOptions}
                  sortKey={gapSortKey}
                  sortDirection={gapSortDirection}
                  onSort={handleGapSort}
                  page={gapPage}
                  totalPages={gapTotalPages}
                  totalItems={filteredGapRows.length}
                  pageSize={gapPageSize}
                  onPageChange={setGapPage}
                  onPageSizeChange={handleGapPageSizeChange}
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <KBUpdatesChart rows={kb_updates_over_time || []} />
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <CountryCoverageLeaderboard rows={countrySummaryRows} />
            <CoverageBubbleChart rows={countrySummaryRows} />
          </div>
          {canManageKnowledgeBase && (
            <CoverageGapHeatmap rows={gaps || []} />
          )}
        </TabsContent>

        <TabsContent value="world">
          <RegionalMapView
            title="Coverage Map"
            description="Knowledge base coverage by country and region for the selected period"
            regions={(mapRegionalCoverage || []).map((region) => ({
              region: region.region,
              value: Number(region.local_docs || 0) + Number(region.global_docs || 0),
              secondaryValue: region.local_docs,
            }))}
            countries={(mapCountryCoverage || []).map((country) => ({
              country: country.country,
              region: country.region,
              value: Number(country.documents || 0),
              secondaryValue: Number(country.pages || 0),
            }))}
            metricLabel="Documents"
            secondaryLabel="Pages"
            timeRangeValue={mapWindow}
            onTimeRangeChange={setMapWindow}
            loading={mapLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
